import React, { useState } from "react";
import { Box, Container, Typography, TextField, Button, Paper, Radio, RadioGroup, FormControlLabel, FormLabel, FormControl, Stack, MenuItem } from "@mui/material";
import emailjs from 'emailjs-com';

const ContactSection = () => {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", contactMethod: "", message: "", userType: "", firmName: "" });
  const [phoneValid, setPhoneValid] = useState(true);
  const [emailValid, setEmailValid] = useState(false);
  const [isPhoneInputActive, setIsPhoneInputActive] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [messageValid, setMessageValid] = useState(false);
  const [messageTouched, setMessageTouched] = useState(false);
  const [contactMethodTouched, setContactMethodTouched] = useState(false);
  const [userTypeTouched, setUserTypeTouched] = useState(false);
  const [firmNameTouched, setFirmNameTouched] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === "phone") {
      const phoneRegex = /^[0-9]{10}$/;
      setPhoneValid(value.length === 0 || phoneRegex.test(value));
      setIsPhoneInputActive(value.length > 0);
    }

    if (name === "email") {
      setEmailValid(value.length === 0 || /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value));
    }

    if (name === "message") {
      setMessageValid(value.trim().length > 0);
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;

    if (name === "email") setEmailTouched(true);
    if (name === "phone") setPhoneTouched(true);
    if (name === "message") setMessageTouched(true);
    if (name === "contactMethod") setContactMethodTouched(true);
    if (name === "userType") setUserTypeTouched(true);
    if (name === "firmName") setFirmNameTouched(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const isFirmRequired = formData.userType === "Attorney/Law Firm";

    if (!emailValid || (formData.phone && !phoneValid) || !messageValid || (isPhoneInputActive && !formData.contactMethod) || !formData.userType || (isFirmRequired && !formData.firmName)) {
      alert("Please fill in all required fields correctly.");
      return;
    }

    const templateParams = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      contactMethod: formData.contactMethod,
      userType: formData.userType,
      firmName: formData.firmName,
      message: formData.message
    };

    emailjs
      .send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        templateParams,
        process.env.REACT_APP_EMAILJS_USER_ID
      )
      .then(
        (response) => {
          console.log("Email sent successfully:", response);
          alert("Your message has been sent successfully!");

          setFormData({ name: "", email: "", phone: "", contactMethod: "", message: "", userType: "", firmName: "" });
          setPhoneValid(true);
          setEmailValid(false);
          setIsPhoneInputActive(false);
          setEmailTouched(false);
          setPhoneTouched(false);
          setMessageValid(false);
          setMessageTouched(false);
          setContactMethodTouched(false);
          setUserTypeTouched(false);
          setFirmNameTouched(false);
        },
        (error) => {
          console.error("Error sending email:", error);
          alert("There was an error sending your message. Please try again later.");
        }
      );
  };

  return (
    <Box id="contact" sx={{ bgcolor: "#f9f9f9", py: { xs: 8, sm: 10, md: 15 } }}>
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
          <Typography variant="h4" gutterBottom>
            Let's Connect
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 1 }}>
            Whether you're navigating divorce or supporting someone who is, I'm here to help. Fill out the form below to start the conversation.
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3, fontStyle: 'italic' }}>
            All inquiries are confidential and reviewed by Karen.
          </Typography>
          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Your Full Name"
                name="name"
                variant="outlined"
                value={formData.name}
                onChange={handleChange}
                required
              />
              <TextField
                fullWidth
                label="Your Email"
                name="email"
                type="email"
                variant="outlined"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                error={emailTouched && !emailValid}
                helperText={emailTouched && !emailValid ? "Please enter a valid email address" : ""}
              />
              <TextField
                fullWidth
                label="Your Phone (Optional)"
                name="phone"
                variant="outlined"
                value={formData.phone}
                onChange={handleChange}
                onBlur={handleBlur}
                error={phoneTouched && !phoneValid}
                helperText={phoneTouched && !phoneValid ? "Please enter a valid 10-digit phone number" : ""}
              />
              {isPhoneInputActive && (
              <FormControl component="fieldset" sx={{ textAlign: 'left' }} error={contactMethodTouched && !formData.contactMethod}>
                <FormLabel component="legend">Preferred Contact Method (required if phone entered)</FormLabel>
                <RadioGroup
                  name="contactMethod"
                  value={formData.contactMethod}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  <FormControlLabel value="Email" control={<Radio />} label="Email" />
                  <FormControlLabel value="Phone" control={<Radio />} label="Phone" />
                </RadioGroup>
                {contactMethodTouched && !formData.contactMethod && (
                  <Typography variant="caption" color="error">Please select a contact method.</Typography>
                )}
              </FormControl>
              )}
              <TextField fullWidth select required label="I am an:"
                name="userType"
                value={formData.userType}
                onChange={handleChange}
                onBlur={handleBlur}
                error={userTypeTouched && !formData.userType}
                helperText={userTypeTouched && !formData.userType ? "Please select an option" : ""}
              >
                <MenuItem value="Individual Seeking Advisory">Individual Seeking Advisory</MenuItem>
                <MenuItem value="Existing Client">Existing Client</MenuItem>
                <MenuItem value="Attorney/Law Firm">Attorney/Law Firm</MenuItem>
                <MenuItem value="Other Professional">Other Professional</MenuItem>
              </TextField>
              {formData.userType === "Attorney/Law Firm" && (
                <TextField
                  fullWidth
                  label="Firm Name"
                  name="firmName"
                  variant="outlined"
                  value={formData.firmName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  error={firmNameTouched && !formData.firmName}
                  helperText={firmNameTouched && !formData.firmName ? "Please enter your firm name" : ""}
                />
              )}
              <TextField
                fullWidth
                label="Tell me a little about your situation"
                name="message"
                variant="outlined"
                multiline
                rows={4}
                value={formData.message}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                error={messageTouched && !messageValid}
                helperText={messageTouched && !messageValid ? "Message is required" : ""}
              />
              <Button type="submit" variant="contained" color="primary" fullWidth>
                Send Message
              </Button>
            </Stack>
          </form>
        </Paper>
      </Container>
    </Box>
  );
};

export default ContactSection;